import React, {useEffect, useMemo, useState} from "react";
import {Dialog, DialogActionsBar} from "@progress/kendo-react-dialogs";
import {Input} from "@progress/kendo-react-inputs";
import {Button} from "@progress/kendo-react-buttons";
import {Grid, GridColumn} from "@progress/kendo-react-grid";
import {process} from "@progress/kendo-data-query";
import {PLATFORM_IDS, SUCCESS_STATUS} from "../../../api/constants";
import {semcastAxios, showErrorToastMessage, showSuccessToastMessage} from "../../../Utils/Common";


function useDebounced(value, delay = 350) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
}

export default function PlatformIdsManagerDialog({
                                                     founderId,
                                                     open,
                                                     onClose,
                                                     onChanged,          // callback to let parent refresh seat cache after changes
                                                 }) {
    const [search, setSearch] = useState("");
    const debounced = useDebounced(search);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [dataState, setDataState] = useState({skip: 0, take: 10, sort: [], filter: null});
    const [selected, setSelected] = useState(null); // row object or null
    const [form, setForm] = useState({
        seat_name: "",
        platform_id: ""
    });
    // near other useState calls
    const [errors, setErrors] = useState({seat_name: "", platform_id: ""});
    const validateSeatName = (name) => {
        const v = (name || "").trim();
        if (!v) return "Seat name is required.";
        if (!/^[A-Za-z0-9]+$/.test(v)) return "Seat name must be alphanumeric (A–Z, 0–9), no spaces or symbols.";
        if (v.length > 64) return "Seat name must be 64 characters or fewer.";
        return "";
    };
    const validatePlatformId = (id) => {
        const value = (id || "").trim();
        if (!value) return "Platform ID is required.";
        if (value.length > 50) return "Platform ID must be 50 characters or fewer.";
        return "";
    };

    useEffect(() => {
        if (open) {
            fetchRows();
        }
    }, [open, debounced]);
    const fetchRows = async () => {
        setLoading(true);
        try {
            const res = await semcastAxios.get(PLATFORM_IDS, {withCredentials: true});
            if (res?.data?.status === SUCCESS_STATUS) {
                setRows(res.data.data || []);
            }
        } catch (e) {
            console.warn(e);
            showErrorToastMessage("Failed to load Platform IDs", 8);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchRows();
        }
    }, [open, debounced]);

    // grid data
    const gridData = useMemo(() => process(rows, dataState), [rows, dataState]);

    const resetForm = () => {
        setSelected(null);
        setForm({seat_name: "", platform_id: ""});
        setErrors({seat_name: "", platform_id: ""});
    };

    const onEditRow = (row) => {
        setSelected(row);
        setForm({
            seat_name: row.seat_name || "",
            platform_id: row.platform_id || ""
        });
    };

    const onSave = async () => {
        const seatErr = validateSeatName(form.seat_name);
        const pidErr = validatePlatformId(form.platform_id);
        if (seatErr || pidErr) {
            setErrors((prev) => ({...prev, seat_name: seatErr, platform_id: pidErr}));
            showErrorToastMessage(seatErr || pidErr, 6);
            return;
        }
        const payload = {...form, seat_name: form.seat_name.trim(), platform_id: form.platform_id.trim()};
        try {
              let res;
              if (selected?.id) {
                    // Update existing Platform ID
                        res = await semcastAxios.put(`${PLATFORM_IDS}/${selected.id}`, payload, {
                          withCredentials: true,
                        });
                    if (res?.data?.status === SUCCESS_STATUS) {
                          showSuccessToastMessage("Updated successfully.");
                          await fetchRows();
                          onChanged?.(res.data.data);
                        } else {
                          showErrorToastMessage("Update failed.", 8);
                        }
                  } else {
                    // Create new Platform ID
                        res = await semcastAxios.post(PLATFORM_IDS, payload, {
                          withCredentials: true,
                        });
                    if (res?.data?.status === SUCCESS_STATUS) {
                          showSuccessToastMessage("Created successfully.");
                          await fetchRows();
                          onChanged?.(res.data.data);
                          resetForm();
                        } else {
                          showErrorToastMessage("Create failed.", 8);
                        }
                  }
        } catch (error) {
            if (error.response) {
                showErrorToastMessage("Save error. " + error.response.data.data, 8);
                console.log("Response data:", error.response.data);
            } else if (error.request) {
                console.log("No response received:", error.request);
            } else {
                console.log("Error setting up request:", error.message);
                showErrorToastMessage("Save error." + error.message, 8);
            }

        }
    };

    const onDelete = async (row) => {
        if (!window.confirm(`Delete seat "${row.seat_name}" (${row.platform_id})?`)) return;
        try {
            const res = await semcastAxios.delete(
                PLATFORM_IDS + `/${row.id}`,
                {withCredentials: true}
            );
            if (res?.data?.status === SUCCESS_STATUS) {
                showSuccessToastMessage("Deleted.");
                await fetchRows();
                onChanged?.({deletedId: row.id});
                if (selected?.id === row.id) resetForm();
            } else {
                showErrorToastMessage("Delete failed.", 8);
            }
        } catch (e) {
            showErrorToastMessage("Delete error.", 8);
        }
    };
    const handleUpdate = async (row) => {
        try {
            // basic validation
            const seatErr = validateSeatName(row.seat_name);
            const pidErr = validatePlatformId(row.platform_id);
            if (seatErr || pidErr) {
                showErrorToastMessage(seatErr || pidErr, 6);
                return;
            }

            const payload = {
                seat_name: row.seat_name.trim(),
                platform_id: row.platform_id.trim(),
            };

            setLoading(true);
            const res = await semcastAxios.put(`${PLATFORM_IDS}/${row.id}`, payload, {
                withCredentials: true,
            });

            if (res?.data?.status === SUCCESS_STATUS) {
                showSuccessToastMessage("Platform ID updated successfully!");
                await fetchRows(); // reload list if you have this helper
                onChanged?.();     // refresh parent cache
            } else {
                showErrorToastMessage("Failed to update platform ID.", 6);
            }
        } catch (err) {
            showErrorToastMessage(err.message || "Error updating platform ID.", 6);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose?.();
    };

    return open ? (
        <Dialog onClose={handleClose} title="Manage Platform IDs" minWidth={920}>
            <div className="k-d-flex k-gap-6 platform-id" style={{alignItems: "stretch", width: "60vw"}}>

                {/* Left: search + list */}
                <div style={{flex: 2, minWidth: 420}}>
                    <div className={"message"}>{`Before deleting a platform id record, must make sure that no activation or segment is using the seat_name or platform_id, unless activation/segment is ended/deleted`}</div>

                    <Grid
                        style={{width: "100%"}}
                        data={gridData}
                        {...dataState}
                        onDataStateChange={(e) => setDataState(e.dataState)}
                        sortable
                        filterable
                        pageable={{pageSizes: [5, 10, 20, 50]}}
                        loading={loading}
                    >
                        <GridColumn field="id" title="ID" filterable={false}
                                    sortable={false}/>
                        <GridColumn field="seat_name" title="Seat Name" filterable={true}
                                    sortable={true}/>
                        <GridColumn field="platform_id" title="Platform ID" filterable={true}
                                    sortable={true}/>
                        <GridColumn
                            title=""
                            width="150px"
                            cell={(td) => (
                                <td>
                                    {td.dataItem.deleted_at == null ?
                                        <>
                                            {/*<Button size="small" look="outline" className="k-ml-2 btn-danger text-white"*/}
                                            {/*        onClick={() => onDelete(td.dataItem)}>*/}
                                            {/*    <i className={"fa fa-trash"}></i>*/}
                                            {/*</Button>*/}
                                            <Button size="small" look="outline" className="k-ml-2"
                                                    onClick={() => onEditRow(td.dataItem)}>Edit</Button></> : "Deleted"}

                                </td>
                            )}
                            filterable={false}
                            sortable={false}
                        />
                    </Grid>
                </div>

                {/* Divider */}
                <div style={{width: 1, background: "var(--kendo-color-border,#e5e7eb)"}}/>

                {/* Right: create/update form */}
                <div style={{flex: 1, minWidth: 220}}>
                    <div
                        className="k-mb-3 k-text-subtle">{selected ? "Update Platform ID" : "Create New Platform ID"}</div>
                    <div className="k-form k-form-md k-mb-4 platform-id-form platform-id-form" style={{height: 'auto'}}>
                        <div className="k-form-field">
                            <label className="k-label required">Seat Name</label>
                            <Input
                                value={form.seat_name}
                                onChange={(e) => {
                                    const val = e.value;
                                    setForm((f) => ({...f, seat_name: val}));
                                    setErrors((prev) => ({...prev, seat_name: validateSeatName(val)}));
                                }}
                            />
                            {errors.seat_name && (
                                <div className="k-text-error" style={{marginTop: 4}}>{errors.seat_name}</div>
                            )}
                        </div>
                        <div className="k-form-field">
                            <label className="k-label required">Platform ID</label>
                            <Input
                                value={form.platform_id}
                                onChange={(e) => {
                                    const val = e.value;
                                    setForm((f) => ({...f, platform_id: val}));
                                    setErrors((prev) => ({...prev, platform_id: validatePlatformId(val)}));
                                }}
                            />
                            {errors.platform_id && (
                                <div className="k-text-error" style={{marginTop: 4}}>
                                    {errors.platform_id}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="k-hstack k-justify-between">
                        <div>
                            {selected && (
                                <Button className={"btn"} look="link" onClick={resetForm}>Switch to Create New</Button>
                            )}
                        </div>
                        <div>
                            <Button className={"btn btn-primary float-right"} onClick={onSave}>{selected ? "Update" : "Create"}</Button>
                        </div>
                    </div>
                </div>
            </div>

            <DialogActionsBar>
                <Button onClick={handleClose}>Close</Button>
            </DialogActionsBar>
        </Dialog>
    ) : null;
}
